import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, expr, leadingTerm, numericExercise, options, pickDistinct, signedConstant, signedTerm, template, trap } from './authoring.ts';

/** Solving quadratics, reading their graphs, completing the square, and the formula that ends the topic. */
export const QUADRATIC_TEMPLATES: ExerciseTemplate[] = [
  template({
    id: 'quadratic-solve-by-factoring',
    conceptId: 'quadratics',
    description: 'Solve a quadratic by factorising and applying the zero-product rule.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 4 ? 9 : 6;
      const r1 = rng.int(1, limit) * (rng.bool() ? 1 : -1);
      // Distinct roots, neither zero, and not opposites — see `pickDistinct`.
      const r2 = pickDistinct(rng, limit, [r1, -r1]);
      const larger = Math.max(r1, r2);
      const smaller = Math.min(r1, r2);
      // x² − (sum)x + (product) = 0 has roots r1 and r2.
      const middle = -(r1 + r2);
      const last = r1 * r2;

      return numericExercise({
        prompt: 'Solve this equation. Give the larger of the two solutions.',
        latex: expr('x^2', signedTerm(middle), signedConstant(last), '= 0'),
        value: larger,
        summary: `It factorises as (x ${signedConstant(-r1)})(x ${signedConstant(-r2)}) = 0, so x = ${r1} or x = ${r2}. The larger is ${larger}.`,
        hint: 'Factorise first. If a product is zero, one of the factors must be zero.',
        steps: [
          { label: 'Factorise.', latex: `(x ${signedConstant(-r1)})(x ${signedConstant(-r2)}) = 0` },
          { label: 'Set each factor to zero.', latex: `x = ${r1} \\text{ or } x = ${r2}` },
          { label: 'Take the larger.', latex: `x = ${larger}` },
        ],
        traps: [trap(smaller, 'only-one-root-given')],
      });
    },
  }),

  template({
    id: 'quadratic-solve-by-roots',
    conceptId: 'quadratics',
    description: 'Solve x² = k, where both signs are solutions.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const root = rng.int(2, difficulty === 2 ? 9 : 15);
      const square = root * root;
      const wantNegative = rng.bool();
      const answer = wantNegative ? -root : root;

      return numericExercise({
        prompt: `Solve this equation. Give the ${wantNegative ? 'negative' : 'positive'} solution.`,
        latex: `x^2 = ${square}`,
        value: answer,
        summary: `Both ${root} and −${root} square to ${square}, so x = ±${root}.`,
        hint: 'How many numbers square to give this? There is more than one.',
        steps: [
          { label: 'Take the square root of both sides.', latex: `x = \\pm\\sqrt{${square}}` },
          { label: 'Both signs work.', latex: `x = ${root} \\text{ or } x = -${root}` },
        ],
        traps: [trap(-answer, 'square-root-sign-missed')],
      });
    },
  }),

  template({
    id: 'quadratic-zero-product',
    conceptId: 'quadratics',
    description: 'Apply the zero-product rule to a factorised equation.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.int(1, 9);
      const q = rng.int(1, 9);
      const answer = Math.max(p, -q);
      const other = Math.min(p, -q);

      return numericExercise({
        prompt: 'This equation is already factorised. Give the larger of its two solutions.',
        latex: `(x - ${p})(x + ${q}) = 0`,
        value: answer,
        summary: `The first bracket is zero at x = ${p}, the second at x = −${q}. The larger is ${answer}.`,
        hint: 'A product is zero only when one of the things being multiplied is zero.',
        steps: [
          { label: 'Set the first factor to zero.', latex: `x - ${p} = 0 \\Rightarrow x = ${p}` },
          { label: 'Set the second factor to zero.', latex: `x + ${q} = 0 \\Rightarrow x = -${q}` },
        ],
        traps: [
          trap(other, 'only-one-root-given'),
          // Reading the roots straight off the brackets without changing sign.
          ...(-p === other ? [] : [trap(-p, 'vertex-sign-flipped')]),
        ],
      });
    },
  }),

  template({
    id: 'quadratic-vertex',
    conceptId: 'quadratic-graphs',
    description: 'Read the vertex from completed-square form.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const h = rng.int(1, 8) * (rng.bool() ? 1 : -1);
      const k = rng.int(-8, 8);
      const wantX = rng.bool();
      const answer = wantX ? h : k;

      return numericExercise({
        prompt: `What is the ${wantX ? 'x' : 'y'}-coordinate of the vertex of this parabola?`,
        latex: `y = (x ${signedConstant(-h)})^2 ${signedConstant(k)}`,
        figure: { kind: 'coordinate-grid', min: -10, max: 10, points: [{ x: h, y: k, label: 'vertex' }] },
        value: answer,
        summary: `The bracket is zero when x = ${h}, and there y = ${k}. So the vertex is at (${h}, ${k}).`,
        hint: 'The squared bracket is never negative, so the smallest y happens where the bracket is zero.',
        steps: [
          { label: 'Find where the bracket is zero.', latex: `x ${signedConstant(-h)} = 0 \\Rightarrow x = ${h}` },
          { label: 'Substitute to find y.', latex: `y = ${k}` },
        ],
        traps: [
          // The sign inside the bracket read straight off.
          ...(wantX && -h !== answer ? [trap(-h, 'vertex-sign-flipped')] : []),
          ...(!wantX && h !== answer ? [trap(h, 'coordinates-reversed')] : []),
        ],
      });
    },
  }),

  template({
    id: 'quadratic-direction',
    conceptId: 'quadratic-graphs',
    description: 'Decide which way a parabola opens, and what that means.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const opensUp = rng.bool();
      const a = (opensUp ? 1 : -1) * rng.int(1, 4);
      const k = rng.int(-6, 6);

      const { choices, correctId } = options(
        [
          {
            label: opensUp ? 'Upwards, so the vertex is the lowest point' : 'Downwards, so the vertex is the highest point',
            correct: true,
          },
          {
            label: opensUp ? 'Downwards, so the vertex is the highest point' : 'Upwards, so the vertex is the lowest point',
            misconceptionId: 'vertex-sign-flipped',
          },
          { label: 'It depends on the constant term', misconceptionId: 'vertex-sign-flipped' },
          { label: 'Neither — the graph is a straight line', misconceptionId: 'exponential-treated-as-linear' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which way does this parabola open?',
        latex: expr('y =', leadingTerm(a, 'x^2'), signedConstant(k)),
        choices,
        correctId,
        summary: `The coefficient of x² is ${a}, which is ${opensUp ? 'positive, so the curve opens upwards' : 'negative, so the curve opens downwards'}.`,
        hint: 'Only the sign of the x² coefficient decides this. Nothing else in the equation matters.',
      });
    },
  }),

  /* ------------------------------------------------ completing the square */
  template({
    id: 'complete-square-constant',
    conceptId: 'completing-the-square',
    description: 'Find the number that completes the square.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      // Even coefficients only: half of an odd number lands on a fraction, and
      // the arithmetic of the fraction is not the skill being tested here.
      const half = rng.int(1, difficulty === 3 ? 10 : 6) * (rng.bool() ? 1 : -1);
      const b = 2 * half;
      const needed = half * half;

      return numericExercise({
        prompt: 'What number must be added to this expression to complete the square?',
        latex: expr('x^2', signedTerm(b)),
        value: needed,
        summary: `Half of ${b} is ${half}, and ${half} squared is ${needed}. That makes the expression (x ${signedConstant(half)})².`,
        hint: 'Halve the coefficient of x. Then do one more thing to it.',
        steps: [
          { label: 'Halve the coefficient of x.', latex: `\\frac{${b}}{2} = ${half}` },
          { label: 'Square that.', latex: `(${half})^{2} = ${needed}` },
          { label: 'The result is a perfect square.', latex: `x^{2} ${signedTerm(b)} + ${needed} = (x ${signedConstant(half)})^{2}` },
        ],
        traps: [
          // Half the coefficient added without ever squaring it.
          trap(half, 'half-coefficient-not-squared'),
        ],
      });
    },
  }),

  template({
    id: 'complete-square-vertex-form',
    conceptId: 'completing-the-square',
    description: 'Complete the square and read off one coordinate of the vertex.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const half = rng.int(1, difficulty === 4 ? 9 : 5) * (rng.bool() ? 1 : -1);
      const b = 2 * half;
      const c = rng.int(-12, 12);
      // x² + bx + c = (x + half)² + (c − half²), so the vertex is (−half, k).
      const k = c - half * half;
      const h = -half;
      const wantX = rng.bool();
      const answer = wantX ? h : k;

      return numericExercise({
        prompt: `Complete the square, then give the ${wantX ? 'x' : 'y'}-coordinate of the vertex.`,
        latex: expr('y = x^2', signedTerm(b), signedConstant(c)),
        value: answer,
        summary: `Half of ${b} is ${half}, so y = (x ${signedConstant(half)})² ${signedConstant(k)} and the vertex is (${h}, ${k}).`,
        hint: 'Halve the coefficient of x for the bracket, then square it and take it back off.',
        steps: [
          { label: 'Halve the coefficient of x.', latex: `${half}` },
          { label: 'Build the bracket, which carries a spare term.', latex: `(x ${signedConstant(half)})^{2} - ${half * half}` },
          { label: 'Combine the constants.', latex: `y = (x ${signedConstant(half)})^{2} ${signedConstant(k)}` },
          { label: 'Read the vertex.', latex: `(${h}, ${k})` },
        ],
        traps: [
          // The (b/2)² added and never taken back off, so the constant is wrong.
          ...(!wantX && c !== answer ? [trap(c, 'square-completed-without-balancing')] : []),
          // The sign inside the bracket read straight off as the x-coordinate.
          ...(wantX && half !== answer ? [trap(half, 'vertex-sign-flipped')] : []),
          // Half the coefficient never squared: k out by half² − half.
          ...(!wantX && c - half !== answer ? [trap(c - half, 'half-coefficient-not-squared')] : []),
        ],
      });
    },
  }),

  template({
    id: 'complete-square-solve',
    conceptId: 'completing-the-square',
    description: 'Solve a quadratic by completing the square, on roots tidy enough to check by factoring.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const half = rng.int(1, difficulty === 5 ? 8 : 5) * (rng.bool() ? 1 : -1);
      /*
       * (x + half)² = root², so x = −half ± root and the constant is
       * half² − root². The root is kept away from |half|, which would make that
       * constant zero: x² + 14x = 0 is a factoring exercise wearing this
       * template's clothes, and teaches nothing about completing anything.
       */
      const root = rng.pick(
        [2, 3, 4, 5, 6, 7, 8, 9].filter((n) => n !== Math.abs(half) && n <= (difficulty === 5 ? 9 : 6)),
      );
      const b = 2 * half;
      const c = half * half - root * root;
      const larger = -half + root;
      const smaller = -half - root;

      return numericExercise({
        prompt: 'Solve by completing the square. Give the larger of the two solutions.',
        latex: expr('x^2', signedTerm(b), signedConstant(c), '= 0'),
        value: larger,
        summary: `Half of ${b} is ${half}, so the equation becomes (x ${signedConstant(half)})² = ${root * root}, giving x = ${-half} ± ${root}. The larger is ${larger}.`,
        hint: 'Move the constant across first, then halve the x-coefficient and square it.',
        steps: [
          { label: 'Move the constant across.', latex: expr('x^2', signedTerm(b), `= ${-c}`) },
          { label: 'Add the square of half the x-coefficient to both sides.', latex: `(x ${signedConstant(half)})^{2} = ${root * root}` },
          { label: 'Take the root of both sides — both signs.', latex: `x ${signedConstant(half)} = \\pm ${root}` },
          { label: 'Solve each.', latex: `x = ${larger} \\text{ or } x = ${smaller}` },
        ],
        traps: [
          trap(smaller, 'only-one-root-given'),
          /*
           * From (x + half)² = root², reading x = half ± root — the sign of the
           * bracket taken as the shift rather than its opposite. Suppressed
           * when it happens to coincide with either real solution.
           */
          ...(half + root === larger || half + root === smaller ? [] : [trap(half + root, 'vertex-sign-flipped')]),
        ],
      });
    },
  }),

  template({
    id: 'complete-square-irrational',
    conceptId: 'completing-the-square',
    description: 'Reach (x + p)² = k on a quadratic that does not factorise, and give the k.',
    difficulties: [4, 5],
    build: ({ rng, difficulty }) => {
      const half = rng.int(1, difficulty === 5 ? 9 : 5) * (rng.bool() ? 1 : -1);
      const b = 2 * half;
      /*
       * k is chosen positive and *not* a perfect square, so the solutions are
       * −half ± √k: genuinely irrational, and unreachable by any search for
       * whole-number factors. That is the case the whole technique exists for,
       * and asking for k rather than for x is what lets a numeric answer box
       * hold an answer that has no exact decimal.
       */
      const candidates = [2, 3, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 17, 18, 19, 20];
      const k = rng.pick(candidates);
      const c = half * half - k;

      return numericExercise({
        prompt: 'Completing the square turns this equation into (x + p)² = k. What is k?',
        latex: expr('x^2', signedTerm(b), signedConstant(c), '= 0'),
        value: k,
        summary: `Half of ${b} is ${half}, so the equation becomes (x ${signedConstant(half)})² = ${k}. Since ${k} is not a perfect square, the solutions are ${-half} ± √${k} — exact, irrational, and beyond the reach of factorising.`,
        hint: 'Move the constant across, then add the square of half the x-coefficient to both sides.',
        steps: [
          { label: 'Move the constant across.', latex: expr('x^2', signedTerm(b), `= ${-c}`) },
          { label: 'Add the square of half the x-coefficient to both sides.', latex: `${-c} + ${half * half} = ${k}` },
          { label: 'The left side is now a perfect square.', latex: `(x ${signedConstant(half)})^{2} = ${k}` },
        ],
        traps: [
          // The balancing term never added to the right-hand side.
          ...(-c === k ? [] : [trap(-c, 'square-completed-without-balancing')]),
          // Half the coefficient added rather than its square.
          ...(-c + half === k || -c + half === -c ? [] : [trap(-c + half, 'half-coefficient-not-squared')]),
        ],
      });
    },
  }),

  template({
    id: 'complete-square-first-step',
    conceptId: 'completing-the-square',
    description: 'Choose the first move when a coefficient stands in front of the x².',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 6);
      const half = rng.int(1, 6);
      const b = a * 2 * half;
      const c = rng.int(1, 12);

      const { choices, correctId } = options(
        [
          { latex: `${a}(x^{2} + ${2 * half}x) + ${c}`, correct: true },
          { latex: `(x + ${b / 2})^{2} + ${c} - ${(b / 2) ** 2}`, misconceptionId: 'leading-coefficient-not-factored' },
          { latex: `${a}(x^{2} + ${2 * half}x + ${c})`, misconceptionId: 'constant-swept-into-bracket' },
          { latex: `${a}x^{2} + ${b}x + ${c} + ${half * half}`, misconceptionId: 'square-completed-without-balancing' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What is the correct first step in completing the square here?',
        latex: expr(`${a}x^2`, signedTerm(b), signedConstant(c)),
        choices,
        correctId,
        summary: `Halving the x-coefficient only works when x² stands alone, so take the ${a} out of the first two terms and leave the ${c} outside.`,
        hint: 'The halve-and-square rule assumes the x² has nothing in front of it. What has to happen before it can be used?',
      });
    },
  }),

  template({
    id: 'quadratic-formula-apply',
    conceptId: 'quadratic-formula',
    description: 'Use the quadratic formula on an equation with whole-number roots.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const r1 = rng.int(1, 7) * (rng.bool() ? 1 : -1);
      const r2 = pickDistinct(rng, 7, [r1, -r1]);
      const a = 1;
      const b = -(r1 + r2);
      const c = r1 * r2;
      const larger = Math.max(r1, r2);
      const discriminant = b * b - 4 * a * c;

      return numericExercise({
        prompt: 'Use the quadratic formula to solve this. Give the larger solution.',
        latex: expr('x^2', signedTerm(b), signedConstant(c), '= 0'),
        value: larger,
        summary: `With a = 1, b = ${b}, c = ${c}, the discriminant is ${discriminant} and the roots are ${r1} and ${r2}.`,
        hint: 'Identify a, b and c first, watching the signs, then substitute into the formula.',
        steps: [
          { label: 'Identify the coefficients.', latex: `a = 1,\\ b = ${b},\\ c = ${c}` },
          { label: 'Compute the discriminant.', latex: `b^2 - 4ac = ${discriminant}` },
          { label: 'Substitute into the formula.', latex: `x = \\frac{${-b} \\pm ${Math.sqrt(discriminant)}}{2}` },
          { label: 'The two roots.', latex: `x = ${r1} \\text{ or } x = ${r2}` },
        ],
        traps: [
          trap(Math.min(r1, r2), 'only-one-root-given'),
          // Dividing only the root term by 2a, leaving −b undivided.
          ...(roundTo(-b + Math.sqrt(discriminant) / 2, 4) === larger
            ? []
            : [trap(roundTo(-b + Math.sqrt(discriminant) / 2, 4), 'formula-denominator-partial')]),
        ],
      });
    },
  }),

  template({
    id: 'discriminant-count-roots',
    conceptId: 'quadratic-formula',
    description: 'Use the discriminant to count the real roots without solving.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const kind = rng.pick(['two', 'one', 'none'] as const);
      const b = rng.int(2, 8) * 2;
      const c = kind === 'two' ? (b * b) / 4 - rng.int(1, 6) : kind === 'one' ? (b * b) / 4 : (b * b) / 4 + rng.int(1, 6);
      const discriminant = b * b - 4 * c;
      const answer = kind === 'two' ? 2 : kind === 'one' ? 1 : 0;

      return numericExercise({
        prompt: 'How many real solutions does this equation have? Answer 0, 1 or 2.',
        latex: `x^2 + ${b}x + ${c} = 0`,
        value: answer,
        summary: `The discriminant is ${b}² − 4(1)(${c}) = ${discriminant}, which is ${discriminant > 0 ? 'positive, so there are two real roots' : discriminant === 0 ? 'zero, so there is exactly one' : 'negative, so there are none'}.`,
        hint: 'You do not need to solve it. Work out b² − 4ac and look at its sign.',
        steps: [
          { label: 'Compute the discriminant.', latex: `b^2 - 4ac = ${discriminant}` },
          {
            label: discriminant > 0 ? 'Positive: the curve crosses twice.' : discriminant === 0 ? 'Zero: the curve touches once.' : 'Negative: the curve never crosses.',
            latex: `${answer} \\text{ real root(s)}`,
          },
        ],
        traps: [...(answer === 2 ? [trap(0, 'discriminant-sign-misread')] : [trap(2, 'discriminant-sign-misread')])],
      });
    },
  }),
];
